// One of the person's own sign-in records, `me/activity/<seq>`
// (01-the-console.md § the address; § the moves, page by page: Your
// activity): what happened, when, through which provider, from where, and
// each further fact the record holds — why it was refused, as which role,
// until when, what it sent. The record is read from the same listing *Your
// activity* draws, and named by its sequence, which the listing answers
// (records/ids.ts; hq decision 0072).

import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { recordProvider } from "../../platform/names.ts";
import { usePage } from "../../platform/session.tsx";
import { Empty, NotAllowed, PageHead, Pending, since, useLoad, when } from "../../ui/components.tsx";
import { Subjects, type SigninsListReply, type SigninsListRequest } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { RecordDetail, shownOf, whatOf } from "../records/SignInRecords.tsx";
import { useSurface } from "../surface.tsx";

/** capital is a phrase with its first letter raised, for a title: *Signed in*. */
function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function RecordPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  const { platform } = usePage();
  const [list] = useLoad("signins", () =>
    s.bus.call<SigninsListReply>(SERVICE, Subjects.signinsList(), {
      limit: 200,
    } satisfies SigninsListRequest),
  );
  const shown =
    list.state === "ready"
      ? shownOf(list.value.records).find((r) => r.id === id && r.record.principal?.subject === s.me)
      : undefined;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "me/activity")}>Your activity</Link>
      <span>/</span>
      <span>{shown === undefined ? "A sign-in" : capital(whatOf(shown.record).word)}</span>
    </>
  );
  if (list.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the sign-in" />
      </>
    );
  }
  if (list.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title="A sign-in" />
        <NotAllowed err={list.err} what="Sign-ins" />
      </>
    );
  }
  if (shown === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title="No such sign-in" />
        <Empty>No sign-in of yours by this id among the latest your activity lists.</Empty>
      </>
    );
  }
  const x = shown.record;
  return (
    <>
      <PageHead crumb={crumb} title={capital(whatOf(x).word)} line={`${when(x.when)} · ${since(x.when)}`} />
      <div className="card">
        <RecordDetail x={x} provider={recordProvider(x, platform.platform_provider)} />
      </div>
    </>
  );
}
